import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const ReadQuestionPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state || {};
  const [questions] = useState(locationState.questions || null);
  const [meta] = useState(locationState.meta || {});
  const [question, setQuestion] = useState(!locationState.questions && locationState ? locationState : null);
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = meta.questionsPerPage || 5; // Default to 5 if not specified

  const getDashboardBasePath = () => {
    if (location.pathname.startsWith('/admin/dashboard')) return '/admin/dashboard';
    if (location.pathname.startsWith('/teachers/dashboard')) return '/teachers/dashboard';
    return '/students/dashboard';
  };

  const openBotPage = (q) => {
    const questionId = q?.id;
    if (!questionId) return;

    const botPath = `${getDashboardBasePath()}/bot/${questionId}`;
    navigate(botPath, {
      state: {
        meta,
        question: q
      }
    });
  };

  useEffect(() => {
    if (!questions && !question && id) {
      api.get(`/questions/${id}`)
        .then(res => setQuestion(res.data))
        .catch(() => {});
    }
  }, [id, question]);

  if (!questions && !question) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center w-full max-w-md">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Loading Questions</h2>
        <p className="text-sm sm:text-base text-gray-600">Please wait while we prepare your questions...</p>
      </div>
    </div>
  );

  // If multiple questions, render all with pagination
  if (questions) {
    const indexOfLastQuestion = currentPage * questionsPerPage;
    const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
    const currentQuestions = questions.slice(indexOfFirstQuestion, indexOfLastQuestion);
    const totalPages = Math.ceil(questions.length / questionsPerPage);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
                <button 
                  onClick={() => navigate(-1)} 
                  className="text-blue-500 hover:text-blue-700 font-medium transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base shrink-0"
                >
                  <span className="text-lg sm:text-xl">←</span>
                  <span>Back</span>
                </button>
                <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                    <span className="text-2xl sm:text-3xl">📖</span>
                    <span>Quiz Review</span>
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 mt-1">Review questions and answers</p>
                </div>
              </div>
              
              <div className="bg-blue-100 text-blue-800 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base w-fit">
                {questions.length} Questions
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
          {/* Quiz Meta Information */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-xl sm:text-2xl mr-3">📋</span>
              Quiz Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              {meta.year && (
                <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm sm:text-base min-w-0">
                  <span className="text-base sm:text-lg">📅</span>
                  <span className="truncate">Year: {meta.year}</span>
                </span>
              )}
              {meta.subject && (
                <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm sm:text-base min-w-0">
                  <span className="text-base sm:text-lg">📚</span>
                  <span className="truncate">Subject: {meta.subject}</span>
                </span>
              )}
              {meta.section && (
                <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm sm:text-base min-w-0">
                  <span className="text-base sm:text-lg">📑</span>
                  <span className="truncate">Section: {meta.section}</span>
                </span>
              )}
              {meta.timeLimit && (
                <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm sm:text-base min-w-0">
                  <span className="text-base sm:text-lg">⏰</span>
                  <span className="truncate">Time Limit: {meta.timeLimit} minutes</span>
                </span>
              )}
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-6">
            {currentQuestions.map((q, idx) => {
              // Parse options if it's a string
              let options = q.options;
              if (typeof options === 'string') {
                try {
                  options = JSON.parse(options);
                } catch (e) {
                  options = [];
                }
              }
              if (!Array.isArray(options)) {
                options = [];
              }

              return (
                <div key={idx} className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="flex-shrink-0">
                      <div className="bg-blue-500 text-white rounded-full w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center font-bold text-sm sm:text-lg">
                        {indexOfFirstQuestion + idx + 1}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                        Question {indexOfFirstQuestion + idx + 1}
                      </h3>
                      <div className="mb-4">
                        <button
                          onClick={() => openBotPage(q)}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium"
                        >
                          <span>🤖</span>
                          <span>Ask Bot</span>
                        </button>
                      </div>
                      
                      {/* Display question images if they exist */}
                      {(() => {
                        // Parse images if it's a string, otherwise use as-is
                        let images = q.images;
                        if (typeof images === 'string') {
                          try {
                            images = JSON.parse(images);
                          } catch (e) {
                            images = [];
                          }
                        }
                        // Ensure it's an array
                        if (!Array.isArray(images)) {
                          images = [];
                        }
                        
                        return images.length > 0 && (
                          <div className="mb-4 flex flex-wrap gap-2">
                            {images.map((imgSrc, imgIdx) => (
                              <img 
                                key={imgIdx}
                                src={imgSrc} 
                                alt={`Question ${indexOfFirstQuestion + idx + 1} visual content ${imgIdx + 1}`}
                                className="w-full sm:w-auto max-w-full max-h-64 object-contain border rounded shadow-sm"
                              />
                            ))}
                          </div>
                        );
                      })()}
                      
                      <p className="text-sm sm:text-base text-gray-800 leading-relaxed mb-4">{q.text}</p>
                      
                      {options.length > 0 ? (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Answer Options:</h4>
                          {options.map((opt, i) => {
                            const isCorrect = opt === q.answer;
                            return (
                              <div key={i} className={`flex items-start sm:items-center p-3 rounded-lg gap-2 ${
                                isCorrect 
                                  ? 'bg-green-100 border-2 border-green-500' 
                                  : 'bg-gray-50 border-2 border-gray-200'
                              }`}>
                                <div className={`w-5 h-5 rounded-full border-2 mt-0.5 sm:mt-0 shrink-0 flex items-center justify-center ${
                                  isCorrect 
                                    ? 'border-green-500 bg-green-500' 
                                    : 'border-gray-300'
                                }`}>
                                  {isCorrect && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </div>
                                <span className={`text-sm sm:text-base ${isCorrect ? 'font-semibold text-green-800' : 'text-gray-700'}`}>
                                  {opt}
                                </span>
                                {isCorrect && (
                                  <span className="ml-auto bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                                    ✓ Correct Answer
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                          <div className="flex items-center">
                            <span className="text-green-500 text-xl mr-3">✓</span>
                            <div>
                              <p className="text-sm font-medium text-green-800">Correct Answer:</p>
                              <p className="text-green-700 font-semibold">{q.answer}</p>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    currentPage === 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white transform hover:scale-105 shadow-lg'
                  }`}
                >
                  <span>←</span>
                  <span>Previous</span>
                </button>
                
                <div className="text-center">
                  <div className="text-sm text-gray-600">Page</div>
                  <div className="text-lg font-bold text-gray-900">{currentPage} of {totalPages}</div>
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    currentPage === totalPages
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white transform hover:scale-105 shadow-lg'
                  }`}
                >
                  <span>Next</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Single question view - Parse options if needed
  let { text, options, answer } = question;
  if (typeof options === 'string') {
    try {
      options = JSON.parse(options);
    } catch (e) {
      options = [];
    }
  }
  if (!Array.isArray(options)) {
    options = [];
  }

  // Parse images for single-question view (same logic as multi-question block)
  let singleImages = question?.images;
  if (typeof singleImages === 'string') {
    try {
      singleImages = JSON.parse(singleImages);
    } catch (e) {
      singleImages = [];
    }
  }
  if (!Array.isArray(singleImages)) {
    singleImages = [];
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
              <button 
                onClick={() => navigate(-1)} 
                className="text-blue-500 hover:text-blue-700 font-medium transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base shrink-0"
              >
                <span className="text-lg sm:text-xl">←</span>
                <span>Back</span>
              </button>
              <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                  <span className="text-2xl sm:text-3xl">📖</span>
                  <span>Question Review</span>
                </h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Review question and answer</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Question Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-8">
          <div className="flex items-start gap-3 sm:gap-4 mb-6">
            <div className="flex-shrink-0">
              <div className="bg-blue-500 text-white rounded-full w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center font-bold text-sm sm:text-lg">
                Q
              </div>
            </div>
            <div className="flex-grow">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Question</h2>
              <div className="mb-4">
                <button
                  onClick={() => openBotPage(question)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium"
                >
                  <span>🤖</span>
                  <span>Ask Bot</span>
                </button>
              </div>
              {/* Single question images (if any) */}
              {singleImages.length > 0 && (
                <div className="mb-4 flex flex-col gap-3">
                  {singleImages.map((imgSrc, imgIdx) => (
                    <img
                      key={imgIdx}
                      src={imgSrc}
                      alt={`Question visual content ${imgIdx + 1}`}
                      className="w-full sm:w-auto max-w-full max-h-64 object-contain border rounded shadow-sm"
                    />
                  ))}
                </div>
              )}

              <p className="text-sm sm:text-lg text-gray-800 leading-relaxed mb-6 whitespace-pre-wrap">{text}</p>

              {options && options.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="text-xl sm:text-2xl mr-3">📝</span>
                    Answer Options
                  </h3>
                  {options.map((opt, idx) => {
                    const isCorrect = opt === answer;
                    return (
                      <div key={idx} className={`flex items-start sm:items-center gap-2 p-3 sm:p-4 rounded-lg transition-all duration-200 ${
                        isCorrect 
                          ? 'bg-green-100 border-2 border-green-500' 
                          : 'bg-gray-50 border-2 border-gray-200'
                      }`}>
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 mt-0.5 sm:mt-0 shrink-0 flex items-center justify-center ${
                          isCorrect 
                            ? 'border-green-500 bg-green-500' 
                            : 'border-gray-300'
                        }`}>
                          {isCorrect && <div className="w-3 h-3 bg-white rounded-full"></div>}
                        </div>
                        <span className={`text-sm sm:text-lg ${isCorrect ? 'font-semibold text-green-800' : 'text-gray-700'}`}>
                          {opt}
                        </span>
                        {isCorrect && (
                          <span className="ml-auto bg-green-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium flex items-center gap-1 whitespace-nowrap">
                            <span>✓</span>
                            <span>Correct Answer</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="flex items-center">
                    <span className="text-green-500 text-2xl mr-4">✓</span>
                    <div>
                      <p className="text-lg font-semibold text-green-800 mb-1">Correct Answer:</p>
                      <p className="text-green-700 font-medium text-lg">{answer}</p>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadQuestionPage;
